import { z } from 'zod';
/**
 * Domain vocabulary - meet-spec.md §4.
 *
 * The constraint union is modelled *fully* here even though only two variants
 * reach the UI in v1. Exposing `price_max` later must be a UI change, not a
 * migration. Discipline is in what you expose, not what you model.
 */
// ─── Activity ───────────────────────────────────────────────────────────────
/**
 * v1 ships only hand-verified types (§16: "Table tennis is not a Google type").
 * Adding one means verifying Google coverage per city by hand first.
 *
 * Verified against live Vienna queries:
 *   beer_garden - 8 results, 1516 Brewing (14200 reviews), Glacis Beisl
 *                 (5456). Real places people actually go to. SHIPPED.
 *   garden      - "Blumenuhr" (36), "Gartensparte Immergrün" (6), "GA
 *                 Kühnplatz" (0). Allotments and flowerbeds. REJECTED.
 *
 * "Rooftop", "outdoors" and "indoors" have no Google place type at all. They
 * would each be a noisy Text Search, which §8 rules out - and as *filters*
 * they fail invariant 8 anyway: a coffee indoors when you wanted a garden is
 * disappointing, not impossible. Promote by §15 evidence, never by guessing.
 */
export const activitySchema = z.enum([
    'coffee',
    'brunch',
    'drinks',
    'dinner',
    'walk',
    'beer_garden',
]);
// ─── Constraints ────────────────────────────────────────────────────────────
/** Vetoes - exposed in v1. Getting these wrong makes the meeting impossible. */
export const accessibleConstraintSchema = z.object({ kind: z.literal('accessible') });
export const dogFriendlyConstraintSchema = z.object({ kind: z.literal('dog_friendly') });
/** Modelled, not exposed. Enters the system via rejection reasons (§7). */
export const minRatingConstraintSchema = z.object({
    kind: z.literal('min_rating'),
    value: z.number().min(0).max(5),
});
export const priceMaxConstraintSchema = z.object({
    kind: z.literal('price_max'),
    value: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
});
export const constraintSchema = z.discriminatedUnion('kind', [
    accessibleConstraintSchema,
    dogFriendlyConstraintSchema,
    minRatingConstraintSchema,
    priceMaxConstraintSchema,
]);
/**
 * The subset Alice may set at creation time. Invariant 8: a constraint reaches
 * the creation screen only if getting it wrong makes the meeting *impossible*.
 */
export const exposedConstraintSchema = z.discriminatedUnion('kind', [
    accessibleConstraintSchema,
    dogFriendlyConstraintSchema,
]);
// ─── Bias ───────────────────────────────────────────────────────────────────
/** Four weight vectors over one pipeline. No extra API calls for any mode. */
export const biasSchema = z.enum([
    'fair',
    'lively',
    'near_creator',
    'near_recipient',
]);
// ─── Rejection reasons ──────────────────────────────────────────────────────
/**
 * Why the suggestions missed.
 *
 * These used to drive a re-rank: a reason spent one of three rejections and
 * fetched the next set. Sitting under the cards they read as 'show me others',
 * which is not what they did, so they moved to the exit - asked once, as
 * someone leaves, and now purely the §15 signal for a bad candidate set.
 */
export const rejectReasonSchema = z.enum([
    'too_far',
    'too_pricey',
    'too_loud',
    'not_my_thing',
]);
/** Chips shown vary by activity - price is meaningless for a walk. */
export const REJECT_CHIPS = {
    coffee: ['too_far', 'too_pricey', 'too_loud'],
    brunch: ['too_far', 'too_pricey', 'too_loud'],
    drinks: ['too_far', 'too_pricey', 'too_loud'],
    dinner: ['too_far', 'too_pricey', 'not_my_thing'],
    walk: ['too_far', 'not_my_thing'],
    beer_garden: ['too_far', 'too_pricey', 'too_loud'],
};
// ─── Intent ─────────────────────────────────────────────────────────────────
export const meetingIntentSchema = z.object({
    activity: activitySchema,
    /** Absolute, with timezone. */
    meetAt: z.string().datetime({ offset: true }),
    bias: biasSchema.default('fair'),
    /** ≤2 exposed in v1. */
    constraints: z.array(exposedConstraintSchema).max(2).default([]),
});
/**
 * How many options the recipient sees at once.
 *
 * Lives here so the API and the web agree on it: the number was written into a
 * German sentence once and went stale the moment it changed, telling people
 * they would get three when they got five.
 *
 * A departure from invariant 5 ("one suggestion at a time"), taken with the
 * tradeoff understood: the spec's bet is that comparison is the disease, so
 * the number to watch is seconds-to-confirmed (§15).
 */
export const VISIBLE_CANDIDATES = 5;
/**
 * How long a meeting is assumed to last, in minutes.
 *
 * Nobody is asked — "how long is coffee" is not a question worth a step, and
 * a calendar entry needs an end. Ninety minutes is long enough not to look
 * like a meeting slot and short enough not to block an evening.
 *
 * Shared so the .ics and the Google Calendar link cannot disagree about when
 * the same meeting ends.
 */
export const MEETING_MINUTES = 90;
// ─── State machine ──────────────────────────────────────────────────────────
/** meet-spec.md §3. */
export const meetingStatusSchema = z.enum([
    'CREATED',
    'AWAITING_ORIGIN',
    'SUGGESTING',
    'CONFIRMED',
    'REMINDED',
    'EXPIRED',
    'ABANDONED',
]);
export const participantRoleSchema = z.enum(['creator', 'recipient']);
export const decisionActionSchema = z.enum([
    'accept',
    'opt_out',
    'manual_pick',
]);
// ─── Geography ──────────────────────────────────────────────────────────────
/**
 * Raw coordinates exist only in memory, only long enough to compute ETAs (§5
 * Privacy). What persists is a geohash at ~500m.
 */
export const coordSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
/** ~500m precision. Geohash length 6 ≈ 1.2km × 0.6km; length 7 ≈ 153m × 153m. */
export const GEOHASH_PRECISION = 6;
export const travelModeSchema = z.enum(['transit', 'walk', 'drive']);
//# sourceMappingURL=domain.js.map